import { useState } from 'react';
import './avatar.css';

export type TamanhoAvatar = 26 | 28 | 32 | 34 | 36 | 48 | 56;

export type AvatarProps = {
  /** Nome completo; as iniciais saem daqui. Vazio ou nulo vira "?". */
  nome?: string | null;
  tamanho?: TamanhoAvatar;
  /** Linha selecionada: o disco vira roxo. */
  destacado?: boolean;
  /**
   * O disco CHEIO: fundo roxo de ação e inicial branca. É o avatar grande do
   * topo do painel do cliente, onde a pessoa é o assunto. Na lista o disco é
   * lavanda com a inicial roxa, para a seleção continuar sendo a coisa mais
   * forte da coluna.
   */
  cheio?: boolean;
  /**
   * Endereço da foto, quando há. Falhando o carregamento — link vencido,
   * contato sem foto (a rota responde 404) —, o disco volta às iniciais.
   *
   * O endereço é sempre NOSSO e assinado; a URL do motor nunca chega aqui.
   */
  foto?: string | null;
  /**
   * De onde sai a cor: o id do contato, quando há. Dois contatos com o mesmo
   * nome ("Cliente Exemplo") ganham cores diferentes, e cada um tem sempre a
   * mesma, em toda tela. Sem ela, a cor sai do nome.
   */
  semente?: string | null;
};

/**
 * Duas iniciais no máximo — a primeira e a última palavra do nome.
 *
 * Nome de uma palavra dá uma inicial; contato sem cadastro dá "?". Três letras
 * num disco de 26px viram borrão, e o nome inteiro está do lado.
 */
export function iniciaisDe(nome?: string | null): string {
  const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  const primeira = partes[0]!.charAt(0);
  const ultima = partes.length > 1 ? partes[partes.length - 1]!.charAt(0) : '';
  return (primeira + ultima).toUpperCase();
}

/** Quantos tons o disco tem (`avatar.css`, `data-tom` 0 a 6). */
const TONS = 7;

/**
 * Qual tom este contato recebe: do id dele (a `semente`) ou, sem id, do nome.
 *
 * Do id porque ele é do contato e não muda: a cor fica "salva" sem gravar nada
 * — a mesma pessoa tem sempre a mesma cor, hoje e amanhã, em toda tela —, e
 * dois contatos de mesmo nome não ficam iguais. FNV-1a: espalha bem textos
 * parecidos (ids, nomes repetidos com um número), e é barato.
 *
 * A v3 tinha ido para um tom só; voltou a variar a pedido do dono do produto
 * (02/10/2026), e ganhou dois tons e o id (26/10/2026): numa fila, discos
 * todos iguais ficavam sem vida.
 */
export function tomDoContato(semente?: string | null): number {
  const texto = (semente ?? '').trim();
  if (texto === '') return 0;
  let hash = 0x811c9dc5;
  for (let i = 0; i < texto.length; i += 1) {
    hash ^= texto.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) % TONS;
}

export function Avatar({
  nome,
  tamanho = 34,
  destacado = false,
  cheio = false,
  foto = null,
  semente = null,
}: AvatarProps) {
  /*
   * Qual endereço falhou, e não um booleano: quando a mesma pessoa ganha um
   * link novo (o anterior venceu em cinco minutos), a foto deve tentar de
   * novo — um "falhou" solto a deixaria nas iniciais para sempre.
   */
  const [falhou, definirFalhou] = useState<string | null>(null);
  const mostraFoto = foto !== null && foto !== '' && falhou !== foto;

  return (
    <span
      className={`avatar avatar--${tamanho}`}
      // O tom sai do id (ou do nome), e some na seleção e no disco cheio — ali o roxo
      // sólido é que marca, e um pastel embaixo dele brigaria.
      data-tom={!destacado && !cheio && !mostraFoto ? tomDoContato(semente ?? nome) : undefined}
      data-destacado={destacado ? 'sim' : undefined}
      data-cheio={cheio ? 'sim' : undefined}
      aria-hidden="true"
    >
      {mostraFoto ? (
        <img
          src={foto}
          alt=""
          loading="lazy"
          decoding="async"
          // A foto vem por link assinado; mandar o endereço da tela como
          // `Referer` não acrescenta nada a quem serve a imagem.
          referrerPolicy="no-referrer"
          onError={() => definirFalhou(foto)}
        />
      ) : (
        iniciaisDe(nome)
      )}
    </span>
  );
}
